import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BatchSetup from './components/batch/BatchSetup';
import SacrificePhase from './components/batch/SacrificePhase';
import BatchSubmission from './components/batch/BatchSubmission';
import BatchComplete from './components/batch/BatchComplete';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Multi-step Batch Price Checker.
 * Steps: setup -> sacrifice -> submit -> complete
 *
 * The batch mechanic (Marcus, 20 Apr):
 *  1. User picks size (100/150/200/250) + companies
 *  2. System returns sacrifice_barcodes per company (16/7/4/0) + real_barcodes
 *  3. User pastes sacrifice barcodes into each 3rd-party site, confirms totals
 *  4. User pastes real barcodes, records the prices they receive
 *  5. Earnings released on 100% completion; held portions forfeit on partial
 */
export default function BatchCheckerPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState('setup');
  const [batch, setBatch] = useState(null);
  const [result, setResult] = useState(null);

  // On mount: auth check + resume in-progress batch if any
  useEffect(() => {
    (async () => {
      try {
        const me = await fetch(`${API}/api/auth/me`, { credentials: 'include' });
        if (!me.ok) return navigate('/login');
        setUser(await me.json());

        const my = await fetch(`${API}/api/batches/my`, { credentials: 'include' });
        if (my.ok) {
          const data = await my.json();
          const active = (data.items || []).find((b) =>
            ['active', 'sacrifice_confirmed'].includes(b.status)
          );
          if (active) {
            setBatch(active);
            setStep(active.status === 'sacrifice_confirmed' ? 'submit' : 'sacrifice');
          }
        }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onBatchStarted = (b) => {
    setBatch(b);
    setStep('sacrifice');
  };
  const onSacrificeConfirmed = () =>
    setStep('submit');
  const onSubmitted = (r) => {
    setResult(r);
    setStep('complete');
  };
  const startOver = () => {
    setBatch(null);
    setResult(null);
    setStep('setup');
  };

  if (loading)
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-400">
        Loading…
      </div>
    );

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200 pb-20">
      <header className="border-b border-zinc-800 bg-black/40">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="text-sm text-zinc-400 hover:text-white"
            data-testid="batch-back"
          >
            ← Dashboard
          </button>
          <h1 className="text-lg font-extrabold text-white">Batch Price Checker</h1>
          <div className="w-20" />
        </div>
        <div className="max-w-3xl mx-auto px-4 pb-3">
          <Stepper step={step} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        {step === 'setup' && <BatchSetup onStarted={onBatchStarted} />}
        {step === 'sacrifice' && batch && (
          <SacrificePhase batch={batch} onConfirmed={onSacrificeConfirmed} onCancel={startOver} />
        )}
        {step === 'submit' && batch && (
          <BatchSubmission batch={batch} onSubmitted={onSubmitted} onCancel={startOver} />
        )}
        {step === 'complete' && result && (
          <BatchComplete result={result} onStartAnother={startOver} />
        )}
      </main>
    </div>
  );
}

function Stepper({ step }) {
  const steps = [
    { key: 'setup', label: '1. Setup' },
    { key: 'sacrifice', label: '2. Sacrifice' },
    { key: 'submit', label: '3. Submit prices' },
    { key: 'complete', label: '4. Done' },
  ];
  const idx = steps.findIndex((s) => s.key === step);
  return (
    <div className="flex items-center gap-1 text-[11px]" data-testid="batch-stepper">
      {steps.map((s, i) => (
        <div key={s.key} className="flex items-center gap-1">
          <span
            className={`px-2 py-1 rounded-full font-bold ${
              i < idx
                ? 'bg-[#00D26A] text-black'
                : i === idx
                ? 'bg-[#FFD700] text-black'
                : 'bg-zinc-800 text-zinc-500'
            }`}
          >
            {s.label}
          </span>
          {i < steps.length - 1 && <span className="text-zinc-700">›</span>}
        </div>
      ))}
    </div>
  );
}
